import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import {
  Paper,
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useSelector } from 'react-redux';

import LayerMapStyles from './LayerMapStyles';
import LayerCard from './LayerCard';

import {
  RootState,
  MapStyleType,
  DashboardLayerConfig,
} from 'types/types';

import 'assets/styles/MarkerTable.scss';

export interface CustomGeoJsonLayer {
  id: string;
  name: string;
  visible: boolean;
}

export interface LayersRef {
  openLayerControl: React.Dispatch<React.SetStateAction<boolean>>;
}

interface LayerProps {
  mapRef: any;
  L: any;
  handleLayersDataEvents: (
    isLayerSelected: boolean,
    layerDefinition: DashboardLayerConfig,
  ) => void;
  tileLayerRef: React.MutableRefObject<any>;
  createTileLayer: (url: string, options?: any) => any;
  selectedMapStyle: React.MutableRefObject<MapStyleType>;
  onUploadGeoJsonLayer?: (id: string, name: string, geojsonData: any) => void;
  onToggleCustomGeoJsonLayer?: (id: string, visible: boolean) => void;
  onRemoveCustomGeoJsonLayer?: (id: string) => void;
}

const Layers: React.ForwardRefRenderFunction<LayersRef, LayerProps> = (
  {
    L,
    mapRef,
    tileLayerRef,
    createTileLayer,
    selectedMapStyle,
    handleLayersDataEvents,
    onUploadGeoJsonLayer,
    onToggleCustomGeoJsonLayer,
    onRemoveCustomGeoJsonLayer,
  },
  ref
) => {
  const {
    zoom, 
    globalLayers,
  } = useSelector((state: RootState) => state.advancedMap);

  const [openLayerControl, setOpenLayerControl] = useState<boolean>(false);
  const [customLayers, setCustomLayers] = useState<CustomGeoJsonLayer[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [layerNameInput, setLayerNameInput] = useState<string>('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const midpoint = Math.ceil(globalLayers.length / 2);
  const leftLayers = globalLayers.slice(0, midpoint);
  const rightLayers = globalLayers.slice(midpoint);

  useImperativeHandle(ref, () => ({
    openLayerControl: setOpenLayerControl
  }));

  const handleOnLayerClick = useCallback((layer: DashboardLayerConfig) => {

    const updatedLayer = {
      ...layer,
      selected: !layer.selected,
    }

    handleLayersDataEvents(
      updatedLayer.selected,
      updatedLayer,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalLayers]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setPendingFile(file);
    setUploadError('');
    if (file && !layerNameInput) {
      setLayerNameInput(file.name.replace(/\.(geojson|json)$/i, ''));
    }
  };

  const handleUploadConfirm = () => {
    if (!pendingFile) {
      setUploadError('Please select a GeoJSON file.');
      return;
    }
    if (!layerNameInput.trim()) {
      setUploadError('Please enter a layer name.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const geojsonData = JSON.parse(event.target?.result as string);
        if (!geojsonData.type) {
          setUploadError('Invalid GeoJSON: missing "type" field.');
          return;
        }
        const id = `custom-geojson-${Date.now()}`;
        const name = layerNameInput.trim();
        const newLayer: CustomGeoJsonLayer = { id, name, visible: true };
        setCustomLayers((prev) => [...prev, newLayer]);
        onUploadGeoJsonLayer?.(id, name, geojsonData);
        setUploadDialogOpen(false);
        setLayerNameInput('');
        setPendingFile(null);
        setUploadError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch {
        setUploadError('Failed to parse file. Please upload a valid GeoJSON.');
      }
    };
    reader.onerror = () => setUploadError('Failed to read file.');
    reader.readAsText(pendingFile);
  };

  const handleDialogClose = () => {
    setUploadDialogOpen(false);
    setLayerNameInput('');
    setPendingFile(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleToggleCustomLayer = (layer: CustomGeoJsonLayer) => {
    const newVisible = !layer.visible;
    setCustomLayers((prev) =>
      prev.map((l) => (l.id === layer.id ? { ...l, visible: newVisible } : l))
    );
    onToggleCustomGeoJsonLayer?.(layer.id, newVisible);
  };

  const handleRemoveCustomLayer = (layer: CustomGeoJsonLayer) => {
    setCustomLayers((prev) => prev.filter((l) => l.id !== layer.id));
    onRemoveCustomGeoJsonLayer?.(layer.id);
  };

  return (
    <>
      {openLayerControl && (
        <Box
          className="layers-popup-backdrop-container"
          onClick={() => setOpenLayerControl(false)}
        >
          <Box className="layers-popup" onClick={(e) => e.stopPropagation()}>
            <Paper>
              <Box className="layers-header">Map Style</Box>
              <Box width={'100%'}>
                <LayerMapStyles
                  L={L}
                  mapRef={mapRef}
                  tileLayerRef={tileLayerRef}
                  createTileLayer={createTileLayer}
                  selectedMapStyle={selectedMapStyle}
                />
              </Box>
              <Box className="layers-header">Map Layers
                <Tooltip title="Upload GeoJSON Layer" placement="top">
                  <IconButton
                    size="small"
                    className="layers-add-btn"
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box className="layers-card-container">
                <Grid container spacing={0}>
                  <Grid item xs={6}>
                    {leftLayers.map((layer, index) => (
                      <LayerCard
                        key={index}
                        layerData={layer}
                        mapZoomLevel={zoom}
                        handleOnLayerClick={handleOnLayerClick}
                      />
                    ))}
                  </Grid>
                  <Grid item xs={6}>
                    {rightLayers.map((layer, index) => (
                      <LayerCard
                        key={index}
                        layerData={layer}
                        mapZoomLevel={zoom}
                        handleOnLayerClick={handleOnLayerClick}
                      />
                    ))}
                  </Grid>
                </Grid>

                {customLayers.length > 0 && (
                  <>
                    <Box className="layers-uploaded-section-label">
                      Uploaded Layers
                    </Box>
                    {customLayers.map((layer) => (
                      <Box
                        key={layer.id}
                        className="layer-card-container layers-custom-card"
                      >
                        <IconButton
                          size="small"
                          className="visibility-button"
                          onClick={() => handleToggleCustomLayer(layer)}
                        >
                          {layer.visible ? (
                            <VisibilityOutlinedIcon />
                          ) : (
                            <VisibilityOffOutlinedIcon />
                          )}
                        </IconButton>
                        <Box className="layer-title layers-custom-card-title">
                          {layer.name}
                        </Box>
                        <Tooltip title="Remove layer" placement="top">
                          <IconButton
                            size="small"
                            className="layers-remove-btn"
                            onClick={() => handleRemoveCustomLayer(layer)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      <Dialog
        open={uploadDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        className="layers-upload-dialog"
      >
        <DialogTitle className="layers-upload-dialog-title">
          Add New Layer
        </DialogTitle>
        <DialogContent>
          <Box className="layers-upload-form">
            <TextField
              label="Layer Name"
              value={layerNameInput}
              onChange={(e) => setLayerNameInput(e.target.value)}
              size="small"
              fullWidth
              placeholder="Enter a name for this layer"
            />
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                size="small"
                className="layers-upload-file-btn"
              >
                {pendingFile ? pendingFile.name : 'Select GeoJSON File'}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".geojson,.json"
                  onChange={handleFileChange}
                />
              </Button>
              {pendingFile && (
                <Typography
                  variant="caption"
                  className="layers-upload-file-size"
                >
                  {(pendingFile.size / 1024).toFixed(1)} KB
                </Typography>
              )}
            </Box>
            {uploadError && (
              <Typography variant="caption" color="error">
                {uploadError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} size="small">
            Cancel
          </Button>
          {pendingFile && (
            <Button
              onClick={handleUploadConfirm}
              variant="contained"
              size="small"
              disabled={!pendingFile}
              className="layers-upload-confirm-btn"
            >
              Add Layer
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default forwardRef<LayersRef, LayerProps>(Layers);
